// Copyright (C) 2021 Bystronic Laser AG

import { BEND_SUMMARY_BASE_COLOR, Color } from '@/utils/color';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { ProcedureName } from './abstract/chartGenerator';
import DoublePieChartKpiWidgetGenerator from './abstract/doublePieChartKpiWidgetGenerator';
import { metricsService } from '@/services/metrics.service';
import { BendingProductivityData } from './chartsData';
import { formatWithUnit } from '@/utils/number';
import { devicesService } from '@/services/devices.service';
import { generatePalette } from '@/components/bend/kpiWidgetUtils';
import i18n from '@/i18n';
import { PieDataItemOption } from 'echarts/types/src/chart/pie/PieSeries';

export default class BendingProductivityKpiWidgetGenerator extends DoublePieChartKpiWidgetGenerator<BendingProductivityData> {
  constructor(procedure: ProcedureName, customerIdDh: number) {
    const mainColor = BEND_SUMMARY_BASE_COLOR;
    super(procedure, customerIdDh, mainColor);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
  ): Promise<BendingProductivityData | null> {
    return await metricsService.getBendingDevicesMetrics<BendingProductivityData>(
      this.procedure,
      {
        customerIdDh: this.customerIdDh,
        deviceStringIds: selectedDevices,
        shiftIds: selectedShifts,
        timeSpan: timeSpan as FilterTimeSpanEnum,
      },
      this.controller,
    );
  }

  private getDeviceNameByStringId(deviceStringId: string): string {
    return devicesService.store.getNameByStringId(deviceStringId)!;
  }

  override formatTooltipValue(value: number): string {
    return formatWithUnit(value, 'h');
  }

  override generateOuterPieSeries(
    data: BendingProductivityData,
  ): PieDataItemOption[] {
    const palette = generatePalette(
      this.mainColor,
      data.deviceBendTimes.length,
    );
    return data.deviceBendTimes.map((deviceEntry, index) => ({
      value: deviceEntry.hours,
      name: this.getDeviceNameByStringId(deviceEntry.deviceStringId),
      itemStyle: {
        color: palette[index],
      },
    }));
  }

  override generateInnerPieSeries(
    data: BendingProductivityData,
  ): PieDataItemOption[] {
    return [
      {
        name: i18n.t(`bend_productivity_widget.bending`).toString(),
        value: data.totalBendTimes.currentTotalHours,
        itemStyle: {
          color: Color.GreenLight,
        },
      },
      {
        name: i18n.t(`bend_productivity_widget.stand-by`).toString(),
        value:
          data.totalBendTimes.currentAvailableHours -
          data.totalBendTimes.currentTotalHours,
        itemStyle: {
          color: Color.Gray,
        },
      },
    ];
  }
}
